import { forwardRef, useImperativeHandle, useEffect } from "react";
import ReactGantt, { Task, Link } from "@dhx/react-gantt";
import "@dhx/react-gantt/dist/react-gantt.css";

import { useTheme } from "../../../shared/context";
import { useGantt } from "../hooks/useGantt";

export interface ProjectGanttHandle {
  expandAll: () => void;
  collapseAll: () => void;
  setZoom: (level: string) => void;
}

interface ProjectGanttProps {
  tasks: Task[];
  links: Link[];
  onTaskDblClick?: (task: Task) => void;
  onAddTask?: (parentTask: Task | null) => void;
  onTaskUpdate?: (task: Task, mode: string) => void;
  onProgressUpdate?: (task: Task) => void;
  onTaskReorder?: (items: { id: number; sequence_order: number }[]) => void;
  onLinkAdd?: (link: Link) => void;
  onLinkDelete?: (linkId: number) => void;
}

export const ProjectGantt = forwardRef<ProjectGanttHandle, ProjectGanttProps>(
  (
    {
      tasks,
      links,
      onTaskDblClick,
      onAddTask,
      onTaskUpdate,
      onProgressUpdate,
      onTaskReorder,
      onLinkAdd,
      onLinkDelete,
    },
    ref
  ) => {
    const { mode } = useTheme();
    const {
      ganttRef,
      setZoom,
      expandAll,
      collapseAll,
      handleTaskDblClick,
      handleBeforeTaskAdd,
      handleAfterTaskDrag,
      handleRowDragEnd,
      handleBeforeRowDragEnd,
      handleAfterLinkAdd,
      handleBeforeLinkDelete,
    } = useGantt({
      onTaskDblClick,
      onAddTask,
      onTaskUpdate,
      onProgressUpdate,
      onTaskReorder,
      onLinkAdd,
      onLinkDelete,
    });

    useEffect(() => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return;

      if ((ganttInstance as any).locale?.labels) {
        (ganttInstance as any).locale.labels.new_task = "New Activity";
      }

      const setTemplates = () => {
        if ((ganttInstance as any).templates) {
          (ganttInstance as any).templates.grid_row_class = (
            start: Date,
            end: Date,
            task: Task
          ) => {
            if (task.type === "milestone") {
              return "gantt-milestone-row";
            }
            return "";
          };
          ganttInstance.render();
        }
      };

      setTemplates();

      const timer = setTimeout(setTemplates, 100);
      return () => clearTimeout(timer);
    }, [tasks, ganttRef]);

    useImperativeHandle(ref, () => ({
      expandAll,
      collapseAll,
      setZoom,
    }));

    const ganttTheme = mode === "dark" ? "dark" : "terrace";

    return (
      <>
        <style>{`
          .gantt-milestone-row .gantt_add,
          .gantt-milestone-row .dhx_gantt_add,
          tr.gantt-milestone-row td:last-child .gantt_add,
          tr.gantt-milestone-row .gantt_add {
            display: none !important;
            visibility: hidden !important;
          }
        `}</style>
        <ReactGantt
          ref={ganttRef}
          tasks={tasks}
          links={links}
          config={{
            scales: [
              { unit: "month", step: 1, format: "%M %Y" },
              { unit: "day", step: 1, format: "%d %M" },
            ],
            scale_height: 60,
            grid_width: 520,
            drag_progress: true,
            drag_resize: true,
            drag_move: true,
            order_branch: true,
            fit_tasks: true,
            show_empty_state: true,
          }}
          plugins={{ tooltip: true }}
          theme={ganttTheme}
          onTaskDblClick={handleTaskDblClick}
          onBeforeTaskAdd={handleBeforeTaskAdd}
          onAfterTaskDrag={handleAfterTaskDrag}
          onRowDragEnd={handleRowDragEnd}
          onBeforeRowDragEnd={handleBeforeRowDragEnd}
          onAfterLinkAdd={handleAfterLinkAdd}
          onBeforeLinkDelete={handleBeforeLinkDelete}
          onBeforeLightbox={() => false}
        />
      </>
    );
  }
);
ProjectGantt.displayName = "ProjectGantt";
